import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams, Navigate } from 'react-router-dom';
import { homeStateActions } from '../../_actions';
import {
  Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, IconButton, InputAdornment,
  MenuItem, Pagination, Select, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, useMediaQuery, Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  AssignmentOutlined,
  CalendarTodayOutlined,
  CheckCircleOutline,
  DescriptionOutlined,
  ErrorOutline,
  PersonOutline,
  RefreshOutlined,
  ReportProblemOutlined,
  SearchOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  getArStatusDataService,
  getAppointmentBillInfoService,
  updateArStatusService,
} from './arReconciliation.service';
import { centralGetSignedUrlService } from '../../_services';
import { hasArAccess, getUserHospitalIds } from './arAccess';
import './ARReconciliation.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const inr = (n) => {
  if (n == null || isNaN(n)) return '₹0';
  return '₹' + Math.round(Number(n)).toLocaleString('en-IN');
};

const fmtDate = (iso) => {
  if (!iso) return '-NA-';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch (e) {
    return iso;
  }
};

// Derive a status pill (label + css class) from a case record
const arStatusInfo = (c) => {
  const status = (c?.hospitalStatus || c?.st || '').toLowerCase();
  if (status === 'approve' || status === 'signed' || status === 'approved') {
    return { label: 'Signed Off', cls: 'signed' };
  }
  if (status === 'dispute' || status === 'disputed') {
    return { label: 'Under Review', cls: 'disputed' };
  }
  return { label: 'Pending', cls: 'verified' };
};

const PAGE_SIZE = 20;

const thSx = {
  fontWeight: 700,
  fontSize: '11px',
  color: '#7a84a3',
  whiteSpace: 'nowrap',
  background: '#f8fafc',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  py: 1.5,
  px: 2,
};

// ─── Left Sidebar ──────────────────────────────────────────────────────────────
const Sidebar = ({ tab, setTab }) => {
  const navItems = [
    {
      key: 'dash',
      label: 'AR Reconciliation',
      icon: <AssignmentOutlined className="ar-nav-icon" />,
    },
    {
      key: 'signed',
      label: 'Signed Off Cases',
      icon: <CheckCircleOutline className="ar-nav-icon" />,
    },
    {
      key: 'disp',
      label: 'Raised Disputes',
      icon: <ReportProblemOutlined className="ar-nav-icon" />,
    },
  ];

  const activeTab = tab;

  return (
    <aside className="ar-sidebar">
      {/* Header */}
      <div className="ar-sidebar-header">
        <div className="ar-sidebar-header-icon">
          <AssignmentOutlined sx={{ fontSize: 18, color: '#fff' }} />
        </div>
        <div>
          <div className="ar-sidebar-title">AR Reconciliation</div>
          <div className="ar-sidebar-subtitle">Hospital Portal</div>
        </div>
      </div>

      {/* Nav */}
      <div className="ar-nav-section">
        <div className="ar-nav-section-label">Navigation</div>
        {navItems.map((item) => (
          <div
            key={item.key}
            className={`ar-nav-item${activeTab === item.key ? ' active' : ''}`}
            onClick={() => setTab(item.key)}
          >
            {item.icon}
            <span className="ar-nav-label">{item.label}</span>
          </div>
        ))}
      </div>
    </aside>
  );
};

// ─── Stats Row ─────────────────────────────────────────────────────────────────
const StatsRow = ({ openCount, signedCount, disputeCount, onTabSelect }) => {
  const stats = [
    {
      key: 'dash',
      label: 'Open Cases',
      value: openCount,
      cls: 'open',
      icon: <AssignmentOutlined sx={{ fontSize: 20, color: '#3D7CC9' }} />,
      iconBg: 'open',
    },
    {
      key: 'signed',
      label: 'Signed Off',
      value: signedCount,
      cls: 'signed',
      icon: <CheckCircleOutline sx={{ fontSize: 20, color: '#137a3f' }} />,
      iconBg: 'signed',
    },
    {
      key: 'disp',
      label: 'Raised Disputes',
      value: disputeCount,
      cls: 'disputed',
      icon: <ReportProblemOutlined sx={{ fontSize: 20, color: '#b75c00' }} />,
      iconBg: 'disputed',
    },
  ];

  return (
    <div className="ar-stats-row">
      {stats.map((s) => (
        <div
          key={s.label}
          className="ar-stat-card"
          style={{ cursor: onTabSelect ? 'pointer' : 'default' }}
          onClick={() => onTabSelect && onTabSelect(s.key)}
        >
          <div className={`ar-stat-icon ${s.iconBg}`}>{s.icon}</div>
          <div className="ar-stat-info">
            <div className={`ar-stat-value ${s.cls}`}>{s.value}</div>
            <div className="ar-stat-label">{s.label}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Supporting Documents Modal ───────────────────────────────────────────────
const DocsModal = ({ open, onClose, attachments = [], appId, patientName }) => {
  const isMobile = useMediaQuery('(max-width:599px)');
  const [loadingIdx, setLoadingIdx]  = useState(null);

  // Flatten attachments into clean document items
  const docList = [];
  if (Array.isArray(attachments) && attachments.length > 0) {
    attachments.forEach((att, attIdx) => {
      const docName = att.key || `Document ${attIdx + 1}`;
      if (Array.isArray(att.urls) && att.urls.length > 0) {
        att.urls.forEach((uObj, uIdx) => {
          docList.push({
            id: `${attIdx}-${uIdx}`,
            title: `${docName}${att.urls.length > 1 ? ` (v${uIdx + 1})` : ''}`,
            url: uObj.url,
            status: uObj.status || 'Available',
            uploader: uObj.uploadedByUsername || '',
            declineReason: uObj.declineReason || '',
            timestamp: uObj.timestamp ? fmtDate(uObj.timestamp) : '',
          });
        });
      } else if (att.url) {
        docList.push({
          id: `${attIdx}`,
          title: docName,
          url: att.url,
          status: 'Available',
        });
      }
    });
  }

  const handleView = async (idx, docUrl) => {
    if (loadingIdx != null) return;
    setLoadingIdx(idx);

    // Pre-open new tab synchronously upon user click so browser popup blocker does not block it
    let newTab = null;
    try {
      newTab = window.open('about:blank', '_blank');
    } catch (e) {
      console.warn('Popup window blocked or error:', e);
    }

    try {
      const res = await centralGetSignedUrlService({ urls: [docUrl] });
      const signedUrl =
        res?.result?.[0] ||
        (Array.isArray(res?.result) ? res.result[0] : null) ||
        (Array.isArray(res) ? res[0] : null) ||
        docUrl;

      if (signedUrl) {
        if (newTab && !newTab.closed) {
          newTab.location.href = signedUrl;
        } else {
          window.open(signedUrl, '_blank');
        }
      } else {
        if (newTab && !newTab.closed) newTab.close();
        toast.error('Unable to fetch signed document URL');
      }
    } catch (err) {
      if (newTab && !newTab.closed) newTab.close();
      console.error('getSignedUrl error:', err);
      toast.error('Failed to open document');
    } finally {
      setLoadingIdx(null);
    }
  };

  const handleClose = () => {
    setLoadingIdx(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : '14px', m: isMobile ? 0 : 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: '15px', borderBottom: '1px solid #e3e8f3', pb: 1.5 }}>
        Supporting Documents
        <Typography sx={{ fontSize: '12px', color: '#7a84a3', fontWeight: 400, mt: 0.3 }}>
          {appId} · {patientName}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: isMobile ? '12px' : '16px 24px' }}>
        {docList.length === 0 ? (
          <div className="ar-panel" style={{ margin: '8px 0', border: '1px dashed #dde3ef' }}>
            <div className="ar-empty">No supporting documents uploaded for this case.</div>
          </div>
        ) : (
          docList.map((doc, i) => (
            <div key={doc.id || i} className="ar-doc-row" style={{ alignItems: 'center' }}>
              <div className="ar-doc-row-left">
                <DescriptionOutlined sx={{ color: '#3D7CC9', fontSize: '20px', flexShrink: 0 }} />
                <div>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0d1f35' }}>
                    {doc.title}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: '#7a84a3' }}>
                    {doc.uploader ? `By: ${doc.uploader}` : ''} {doc.timestamp ? `· ${doc.timestamp}` : ''}
                  </Typography>
                  {doc.declineReason && (
                    <Typography sx={{ fontSize: '11px', color: '#b75c00', mt: 0.3, background: '#fff4e5', p: '2px 6px', borderRadius: '4px' }}>
                      Reason: {doc.declineReason}
                    </Typography>
                  )}
                </div>
                <span className={`ar-pill ${doc.status === 'Approved' ? 'signed' : (doc.status === 'Declined' ? 'disputed' : 'verified')}`}>
                  {doc.status}
                </span>
              </div>
              <Button
                size="small"
                variant="outlined"
                disabled={loadingIdx === i}
                onClick={() => handleView(i, doc.url)}
                startIcon={
                  loadingIdx === i ? (
                    <CircularProgress size={13} color="inherit" />
                  ) : (
                    <VisibilityOutlined sx={{ fontSize: '14px' }} />
                  )
                }
                sx={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  flexShrink: 0,
                  minWidth: '85px',
                  borderColor: '#3D7CC9',
                  color: '#3D7CC9',
                  '&:hover': { borderColor: '#2d6bb5', background: '#eef4ff' },
                  '&.Mui-disabled': { borderColor: '#dde3ef', color: '#7a84a3' },
                }}
              >
                {loadingIdx === i ? 'Opening...' : 'View'}
              </Button>
            </div>
          ))
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid #e3e8f3', p: '12px 16px' }}>
        <Button onClick={handleClose} sx={{ textTransform: 'none', borderRadius: '8px' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Case Detail View ───────────────────────────────────────────────────────────
const ARDetail = ({
  appId,
  caseSummary,
  hospitalId,
  fromTab = 'dash',
  onBack,
  onCaseActionSuccess,
}) => {
  const isMobile = useMediaQuery('(max-width:599px)');
  const [loading, setLoading]       = useState(true);
  const [billData, setBillData]     = useState(null);
  const [modal, setModal]           = useState(null); // 'sign' | 'disp'
  const [remarks, setRemarks]       = useState('');
  const [docsOpen, setDocsOpen]     = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const tabTitleMap = {
    dash: 'AR Reconciliation',
    signed: 'Signed Off Cases',
    disp: 'Raised Disputes',
  };
  const fromTabTitle = tabTitleMap[fromTab] || 'AR Reconciliation';

  // Fetch full details for this appointment
  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    getAppointmentBillInfoService(appId)
      .then((res) => {
        if (!isMounted) return;
        if (res && res.data) {
          setBillData(res.data);
        }
      })
      .catch((err) => {
        console.error('getAppointmentBillInfo error:', err);
        toast.error('Could not load bill info, showing summary data');
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [appId]);

  // Derived fields from API
  const patientName   = billData?.patientName || caseSummary?.patientName || caseSummary?.pt || '-NA-';
  const hospitalName  = billData?.hospitalName || caseSummary?.hospitalName || '-NA-';
  const doctorName    = billData?.doctorName || '-NA-';
  const diseaseName   = billData?.diseaseName || '-NA-';
  const categoryName  = billData?.categoryName || '-NA-';
  const paymentMode   = billData?.PaymentMode || '-NA-';
  const lensDetails   = [billData?.lensBrand, billData?.lensModel].filter(Boolean).join(' - ') || '-NA-';
  const dateAdm       = fmtDate(billData?.dateOfAdmission || caseSummary?.dateOfAdmission || caseSummary?.apd);
  const dateDsc       = fmtDate(billData?.dateOfDischarge);
  const finalBillAmt  = billData?.netBillAmount != null ? billData.netBillAmount : (caseSummary?.netBillAmount || 0);

  const calc          = billData?.calculation || {};
  const calcInputs    = calc.inputs || {};
  const calcResult    = calc.result || {};

  const hospitalShare = calcResult.hospital_share_total != null ? calcResult.hospital_share_total : (caseSummary?.hospital_share_total ?? 0);
  const ghvShare      = calcResult.ghv_share_total != null ? calcResult.ghv_share_total : (caseSummary?.ghv_share_total ?? 0);
  const settledAmt    = calcResult.settled_amount != null ? calcResult.settled_amount : finalBillAmt;
  const hvCost        = calcResult.hv_cost != null ? calcResult.hv_cost : (calcResult.lens_cost || 0);
  const equipHire     = calcResult.equipment_hire_charges || 0;
  const sharingAmt    = calcResult.sharing_amount || 0;
  const baseShare     = calcResult.hospital_share_base || 0;
  const baseBasis     = calcResult.hospital_share_basis || '';
  const addonsTotal   = calcResult.addons_total || 0;
  const procuredTotal = calcResult.procured_total || 0;

  const steps         = calcResult.calculation_steps || [];
  const notes         = Array.isArray(billData?.notes) ? billData.notes : [];
  const attachments   = billData?.attachments || [];
  const totalDocsCount = attachments.reduce((sum, att) => {
    if (Array.isArray(att.urls)) return sum + att.urls.length;
    if (att.url) return sum + 1;
    return sum;
  }, 0);

  const isAlreadySigned   = caseSummary?.hospitalStatus === 'approve' || caseSummary?.st === 'signed';
  const isAlreadyDisputed = caseSummary?.hospitalStatus === 'dispute' || caseSummary?.st === 'disputed';

  // Handle Sign Off
  const handleSignOff = async () => {
    setSubmitting(true);
    try {
      await updateArStatusService({
        appId,
        hospitalId: hospitalId || billData?.hospitalId || caseSummary?.hospitalId,
        hospitalStatus: 'approve',
        remark: 'Approved by hospital',
      });
      toast.success(`Case ${appId} signed off successfully!`);
      setModal(null);
      if (onCaseActionSuccess) onCaseActionSuccess(appId, 'approve');
      onBack();
    } catch (err) {
      console.error('Sign off error:', err);
      toast.error(err?.message || 'Failed to sign off case');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Dispute
  const handleDispute = async () => {
    if (!remarks.trim()) return;
    setSubmitting(true);
    try {
      await updateArStatusService({
        appId,
        hospitalId: hospitalId || billData?.hospitalId || caseSummary?.hospitalId,
        hospitalStatus: 'dispute',
        remark: remarks.trim(),
      });
      toast.info(`Dispute submitted for ${appId}`);
      setModal(null);
      setRemarks('');
      if (onCaseActionSuccess) onCaseActionSuccess(appId, 'dispute', remarks.trim());
      onBack();
    } catch (err) {
      console.error('Dispute error:', err);
      toast.error(err?.message || 'Failed to submit dispute');
    } finally {
      setSubmitting(false);
    }
  };

  const infoItem = (label, value, sx = {}) => (
    <div className="ar-info-item">
      <small>{label}</small>
      <b style={sx}>{value}</b>
    </div>
  );

  return (
    <>
      {/* Prominent Back Button & Breadcrumbs */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack sx={{ fontSize: '18px' }} />}
          onClick={onBack}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '13px',
            borderRadius: '8px',
            borderColor: '#dde3ef',
            color: '#00263E',
            background: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            px: 2,
            py: 0.6,
            '&:hover': {
              background: '#f4f6fa',
              borderColor: '#3D7CC9',
              color: '#3D7CC9',
            },
          }}
        >
          Back
        </Button>
        <div className="ar-breadcrumb" style={{ margin: 0 }}>
          <span className="link" onClick={onBack}>{fromTabTitle}</span>
          <span>›</span>
          <span style={{ color: '#0d1f35', fontWeight: 600 }}>{appId}</span>
          {isAlreadySigned && <span className="ar-pill signed" style={{ marginLeft: 8 }}>Signed Off</span>}
          {isAlreadyDisputed && <span className="ar-pill disputed" style={{ marginLeft: 8 }}>Dispute Raised</span>}
        </div>
      </Box>

      {loading ? (
        <div className="ar-loading-box">
          <CircularProgress size={36} sx={{ color: '#3D7CC9' }} />
          <span>Loading appointment bill information for {appId}...</span>
        </div>
      ) : (
        <>
          {/* Patient & Visit Info */}
          <div className="ar-panel">
            <div className="ar-panel-title">Patient &amp; Visit Information</div>
            <div className="ar-info-grid">
              {infoItem('Date of Admission',      dateAdm)}
              {infoItem('Date of Discharge',      dateDsc)}
              {infoItem('Hospital Name',          hospitalName)}
              {infoItem('Doctor Name',            doctorName)}
              {infoItem('Disease Name',           diseaseName)}
              {infoItem('Procedure / Category',   categoryName)}
              {infoItem('Payment Mode',           paymentMode, { color: '#5a12cc', fontWeight: 700 })}
              {infoItem('Lens Details',           lensDetails)}
              {infoItem('Patient Name',           patientName)}
              {infoItem('Final Bill Amount',      inr(finalBillAmt))}
              {infoItem('Extra Cash / Refund',    calcInputs.Extra_Cash != null ? inr(calcInputs.Extra_Cash) : '-NA-')}
              {infoItem('Approved Amount',        calcInputs.Approved_Amount != null ? inr(calcInputs.Approved_Amount) : '-NA-')}
            </div>
          </div>

          {/* Share cards */}
          <div className="ar-cards-row">
            <div className="ar-share-card hospital">
              <small>Hospital Share</small>
              <div className="ar-amount">{inr(hospitalShare)}</div>
            </div>
            <div className="ar-share-card ghv">
              <small>GHV Share</small>
              <div className="ar-amount">{inr(ghvShare)}</div>
            </div>
          </div>

          {/* Calculation Summary */}
          <div className="ar-panel">
            <div className="ar-panel-title">Calculation Summary</div>
            <div className="ar-calc-grid">
              <span className="ar-label">Settled Amount</span>
              <span className="ar-value">{inr(settledAmt)}</span>

              <span className="ar-label">HV Cost / Implants</span>
              <span className="ar-value">{inr(hvCost)}</span>

              <span className="ar-label">Equipment Hire</span>
              <span className="ar-value">{inr(equipHire)}</span>

              <span className="ar-label">Sharing Amount</span>
              <span className="ar-value">{inr(sharingAmt)}</span>

              <span className="ar-label">Hospital Share Base</span>
              <span className="ar-value">{inr(baseShare)}</span>

              {baseBasis && (
                <>
                  <span className="ar-label">Basis of Sharing</span>
                  <span className="ar-value" style={{ fontSize: '11px', color: '#7a84a3', fontWeight: 500 }}>
                    {baseBasis}
                  </span>
                </>
              )}

              <span className="ar-label">Add-ons Total</span>
              <span className="ar-value">{inr(addonsTotal)}</span>

              <span className="ar-label">Procured Total (Hospital)</span>
              <span className="ar-value">{inr(procuredTotal)}</span>

              <Divider sx={{ gridColumn: '1/-1', my: 0.5, borderColor: '#e3e8f3' }} />

              <span className="ar-label ar-total-row" style={{ fontWeight: 700 }}>Hospital Share</span>
              <span className="ar-value ar-total-row" style={{ color: '#5a12cc', fontSize: '16px' }}>{inr(hospitalShare)}</span>

              <span className="ar-label" style={{ fontWeight: 700 }}>GHV Share</span>
              <span className="ar-value" style={{ color: '#008a6e', fontSize: '16px' }}>{inr(ghvShare)}</span>
            </div>
          </div>

          {/* Step-by-step Calculation */}
          {steps.length > 0 && (
            <div className="ar-panel">
              <div className="ar-panel-title">Calculation Detail — Step by Step</div>
              {steps.map((st, i) => (
                <div key={i} className={`ar-step ${st.highlight || ''}`}>
                  <div className="ar-step-title">{st.title}</div>
                  <div className="ar-step-body">{st.formula}</div>
                </div>
              ))}
            </div>
          )}

          {/* Notes & Observations from EMR / Extraction */}
          {notes.length > 0 && (
            <div className="ar-panel">
              <div className="ar-panel-title">Notes &amp; Observations</div>
              <div className="ar-notes-list">
                {notes.map((note, idx) => (
                  <div key={idx} className="ar-note-item">
                    <span className="ar-note-bullet">•</span>
                    <span>{note}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="ar-actions">
            {!isAlreadySigned && !isAlreadyDisputed && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleOutline />}
                  onClick={() => setModal('sign')}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '10px',
                    fontWeight: 600,
                    px: 3,
                    boxShadow: '0 2px 10px rgba(19,122,63,0.25)',
                    ...(isMobile ? { flex: 1 } : {}),
                  }}
                >
                  Sign Off
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<ErrorOutline />}
                  onClick={() => setModal('disp')}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '10px',
                    fontWeight: 600,
                    px: 3,
                    ...(isMobile ? { flex: 1 } : {}),
                  }}
                >
                  Raise Dispute
                </Button>
              </>
            )}

            <Button
              variant="outlined"
              startIcon={<DescriptionOutlined />}
              onClick={() => setDocsOpen(true)}
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
                fontWeight: 600,
                px: 3,
                borderColor: '#3D7CC9',
                color: '#3D7CC9',
                '&:hover': { background: '#eef4ff', borderColor: '#3D7CC9' },
                ...(isMobile ? { width: '100%' } : {}),
              }}
            >
              View Supporting Documents ({totalDocsCount || '0'})
            </Button>
          </div>

          {/* Sign-off confirmation modal */}
          <Dialog
            open={modal === 'sign'}
            onClose={() => !submitting && setModal(null)}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: '14px', m: isMobile ? 1.5 : 2 } }}
          >
            <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Sign off {appId}?</DialogTitle>
            <DialogContent>
              <Typography sx={{ fontSize: '14px', color: '#7a84a3', lineHeight: 1.6 }}>
                The case will move to <b>Signed Off Cases</b>. This action confirms your approval of the final calculations.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: '12px 20px', gap: 1 }}>
              <Button
                disabled={submitting}
                onClick={() => setModal(null)}
                sx={{ textTransform: 'none', borderRadius: '8px', color: '#7a84a3' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="success"
                disabled={submitting}
                onClick={handleSignOff}
                startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
                sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700, px: 3 }}
              >
                {submitting ? 'Signing Off...' : 'Confirm Sign Off'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Dispute modal */}
          <Dialog
            open={modal === 'disp'}
            onClose={() => !submitting && setModal(null)}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{ sx: { borderRadius: isMobile ? 0 : '14px', m: isMobile ? 0 : 2 } }}
          >
            <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid #e3e8f3', pb: 1.5 }}>
              Raise Dispute · {appId}
              <Typography sx={{ fontSize: '12px', color: '#7a84a3', fontWeight: 400, mt: 0.3 }}>
                {patientName}
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: '18px !important' }}>
              <Typography sx={{ fontSize: '13px', mb: 1.5, fontWeight: 600, color: '#0d1f35' }}>
                Remarks <span style={{ color: '#c62828' }}>*</span>
                <span style={{ color: '#7a84a3', fontWeight: 400 }}> (mandatory reason for dispute)</span>
              </Typography>
              <textarea
                className="ar-remarks-textarea"
                rows={5}
                maxLength={500}
                placeholder="Explain why you disagree with this calculation..."
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
              <Typography sx={{ fontSize: '11px', color: '#7a84a3', mt: 0.5, textAlign: 'right' }}>
                {remarks.length}/500
              </Typography>
            </DialogContent>
            <DialogActions sx={{ borderTop: '1px solid #e3e8f3', p: '12px 20px', gap: 1 }}>
              <Button
                disabled={submitting}
                onClick={() => { setModal(null); setRemarks(''); }}
                sx={{ textTransform: 'none', borderRadius: '8px', color: '#7a84a3' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={!remarks.trim() || submitting}
                onClick={handleDispute}
                startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
                sx={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  fontWeight: 700,
                  px: 3,
                  background: '#3D7CC9',
                  '&:hover': { background: '#2d6bb5' },
                }}
              >
                {submitting ? 'Submitting...' : 'Submit Dispute'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Supporting Docs Modal */}
          <DocsModal
            open={docsOpen}
            onClose={() => setDocsOpen(false)}
            attachments={attachments}
            appId={appId}
            patientName={patientName}
          />
        </>
      )}
    </>
  );
};

// ─── Shared Pagination Bar ──────────────────────────────────────────────────
const PaginationBar = ({ page, totalPages, onChange }) =>
  totalPages <= 1 ? null : (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2, borderTop: '1px solid #edf0f7', mt: 0.5 }}>
      <Pagination
        count={totalPages}
        page={page}
        onChange={(_, val) => onChange(val)}
        color="primary"
        size="small"
        showFirstButton
        showLastButton
        sx={{
          '& .MuiPaginationItem-root': { fontSize: '13px', fontWeight: 600, borderRadius: '8px' },
          '& .Mui-selected': { background: '#3D7CC9 !important', color: '#fff' },
        }}
      />
    </Box>
  );

// ─── Main Dashboard (AR Reconciliation List) ──────────────────────────────────
const ARDashboard = ({
  cases,
  loading,
  onRefresh,
  onView,
  openCount,
  signedCount,
  disputeCount,
  onTabSelect,
  hospitalSelector,
}) => {
  const isMobile = useMediaQuery('(max-width:599px)');
  const [filters, setFilters] = useState({ app: '', pt: '', from: '', to: '' });
  const [page, setPage] = useState(1);

  const upd = (key) => (e) => { setPage(1); setFilters((p) => ({ ...p, [key]: e.target.value })); };
  const reset = () => { setPage(1); setFilters({ app: '', pt: '', from: '', to: '' }); };

  const filtered = cases.filter((c) => {
    const appId = c.appId || c.app || '';
    const pt    = c.patientName || c.pt || '';
    const date  = c.dateOfAdmission ? c.dateOfAdmission.split('T')[0] : (c.apd || '');

    const matchesApp = !filters.app || appId.toLowerCase().includes(filters.app.toLowerCase());
    const matchesPt  = !filters.pt  || pt.toLowerCase().includes(filters.pt.toLowerCase());
    const matchesFrom = !filters.from || date >= filters.from;
    const matchesTo   = !filters.to   || date <= filters.to;

    return matchesApp && matchesPt && matchesFrom && matchesTo;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      background: '#f8fafc',
      fontSize: '13px',
      '& fieldset': { borderColor: '#dde3ef' },
      '&:hover fieldset': { borderColor: '#3D7CC9' },
      '&.Mui-focused fieldset': { borderColor: '#3D7CC9', borderWidth: '1.5px' },
    },
    '& .MuiInputLabel-root': { fontSize: '13px', color: '#7a84a3' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#3D7CC9' },
  };

  return (
    <>
      <div className="ar-page-header">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap', gap: 1 }}>
          <div>
            <Typography className="ar-page-title">AR Reconciliation</Typography>
            <Typography className="ar-page-sub">
              Cases shared by Pristyn Care awaiting your review and sign-off
            </Typography>
          </div>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
            {hospitalSelector}
            <Tooltip title="Refresh list">
              <IconButton onClick={onRefresh} sx={{ color: '#3D7CC9', background: '#fff', border: '1px solid #dde3ef' }}>
                <RefreshOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </div>

      <StatsRow
        openCount={openCount}
        signedCount={signedCount}
        disputeCount={disputeCount}
        onTabSelect={onTabSelect}
      />

      {/* Filter Card */}
      <div className="ar-filter-card">
        <div className="ar-filter-title">
          <SearchOutlined sx={{ fontSize: 14, color: '#7a84a3' }} />
          Filter Cases
        </div>
        <div className="ar-filter-row">
          <TextField
            label="App ID"
            placeholder="e.g. bopYPbEp25"
            size="small"
            value={filters.app}
            onChange={upd('app')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ fontSize: 16, color: '#7a84a3' }} />
                </InputAdornment>
              ),
            }}
            sx={{ ...inputSx, minWidth: isMobile ? '100%' : 170, flex: isMobile ? 'none' : 1 }}
          />
          <TextField
            label="Patient Name"
            placeholder="Search patient"
            size="small"
            value={filters.pt}
            onChange={upd('pt')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutline sx={{ fontSize: 16, color: '#7a84a3' }} />
                </InputAdornment>
              ),
            }}
            sx={{ ...inputSx, minWidth: isMobile ? '100%' : 180, flex: isMobile ? 'none' : 1 }}
          />
          <TextField
            label="Admission Date (From)"
            type="date"
            size="small"
            value={filters.from}
            onChange={upd('from')}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarTodayOutlined sx={{ fontSize: 14, color: '#7a84a3' }} />
                </InputAdornment>
              ),
            }}
            sx={{ ...inputSx, minWidth: isMobile ? '100%' : 195 }}
          />
          <TextField
            label="Admission Date (To)"
            type="date"
            size="small"
            value={filters.to}
            onChange={upd('to')}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarTodayOutlined sx={{ fontSize: 14, color: '#7a84a3' }} />
                </InputAdornment>
              ),
            }}
            sx={{ ...inputSx, minWidth: isMobile ? '100%' : 195 }}
          />
          <Button
            variant="outlined"
            onClick={reset}
            sx={{
              height: '40px',
              textTransform: 'none',
              borderRadius: '10px',
              borderColor: '#dde3ef',
              color: '#7a84a3',
              fontSize: '13px',
              fontWeight: 600,
              px: 2.5,
              '&:hover': { borderColor: '#3D7CC9', color: '#3D7CC9', background: '#eef4ff' },
              ...(isMobile ? { width: '100%' } : {}),
            }}
          >
            Reset Filters
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="ar-loading-box">
          <CircularProgress size={32} sx={{ color: '#3D7CC9' }} />
          <span>Fetching AR Reconciliation cases...</span>
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="ar-mobile-cards">
            {filtered.length === 0 ? (
              <div className="ar-panel"><div className="ar-empty">No pending cases match your filters</div></div>
            ) : (
              <>
                {paginated.map((c) => {
                  const appId = c.appId || c.app;
                  const pt    = c.patientName || c.pt;
                  const apd   = fmtDate(c.dateOfAdmission || c.apd);
                  const hs    = c.hospital_share_total != null ? c.hospital_share_total : (c.hs || 0);
                  const gs    = c.ghv_share_total != null ? c.ghv_share_total : (c.gs || 0);

                  const st    = arStatusInfo(c);

                  return (
                    <div key={appId} className="ar-case-card" onClick={() => onView(appId, c)} style={{ cursor: 'pointer' }}>
                      <div className="ar-case-card-header">
                        <div>
                          <div className="ar-case-card-app">{appId}</div>
                          <div className="ar-case-card-patient">{pt}</div>
                          <div className="ar-case-card-date">{apd}</div>
                        </div>
                        <span className={`ar-pill ${st.cls}`}>{st.label}</span>
                      </div>
                      <div className="ar-case-card-shares">
                        <div className="ar-case-card-share"><small>Hospital Share</small><b>{inr(hs)}</b></div>
                        <div className="ar-case-card-share"><small>GHV Share</small><b>{inr(gs)}</b></div>
                      </div>
                    </div>
                  );
                })}
                <PaginationBar page={page} totalPages={totalPages} onChange={setPage} />
              </>
            )}
          </div>

          {/* Desktop table */}
          <div className="ar-table-card">
            <div className="ar-table-header">
              <span className="ar-table-header-title">
                {filtered.length} case{filtered.length !== 1 ? 's' : ''} found
                {totalPages > 1 && <span style={{ fontWeight: 400, color: '#7a84a3', fontSize: '12px', marginLeft: 8 }}>· Page {page} of {totalPages}</span>}
              </span>
            </div>
            <div className="ar-table-wrap">
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['App ID', 'Patient Name', 'Admission Date', 'Hospital Share', 'GHV Share', 'Status', 'Action'].map(
                        (h) => <TableCell key={h} sx={thSx}>{h}</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="ar-empty">
                          No pending cases match your filters
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginated.map((c, idx) => {
                        const appId = c.appId || c.app;
                        const pt    = c.patientName || c.pt;
                        const apd   = fmtDate(c.dateOfAdmission || c.apd);
                        const hs    = c.hospital_share_total != null ? c.hospital_share_total : (c.hs || 0);
                        const gs    = c.ghv_share_total != null ? c.ghv_share_total : (c.gs || 0);
                        const st    = arStatusInfo(c);

                        return (
                          <TableRow
                            key={appId}
                            hover
                            sx={{
                              '&:last-child td': { borderBottom: 0 },
                              background: idx % 2 === 0 ? '#fff' : '#fafbfd',
                              '&:hover': { background: '#eef4ff !important' },
                              cursor: 'pointer',
                            }}
                            onClick={() => onView(appId, c)}
                          >
                            <TableCell sx={{ color: '#3D7CC9', fontWeight: 700, fontSize: '13px', py: 1.8, px: 2 }}>
                              {appId}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 500, fontSize: '13px', py: 1.8, px: 2 }}>
                              {pt}
                            </TableCell>
                            <TableCell sx={{ color: '#7a84a3', fontSize: '13px', py: 1.8, px: 2 }}>
                              {apd}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '13px', color: '#5a12cc', py: 1.8, px: 2 }}>
                              {inr(hs)}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '13px', color: '#008a6e', py: 1.8, px: 2 }}>
                              {inr(gs)}
                            </TableCell>
                            <TableCell sx={{ py: 1.8, px: 2 }}>
                              <span className={`ar-pill ${st.cls}`}>{st.label}</span>
                            </TableCell>
                            <TableCell sx={{ py: 1.8, px: 2 }}>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onView(appId, c);
                                }}
                                sx={{
                                  textTransform: 'none',
                                  borderRadius: '8px',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  background: '#3D7CC9',
                                  px: 2,
                                  boxShadow: '0 2px 8px rgba(61,124,201,0.3)',
                                  '&:hover': { background: '#2d6bb5' },
                                }}
                              >
                                View Details
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <PaginationBar page={page} totalPages={totalPages} onChange={setPage} />
            </div>
          </div>
        </>
      )}
    </>
  );
};

// ─── Signed Off Cases View ────────────────────────────────────────────────────
const ARSignedOff = ({ cases, loading, onRefresh, onView, hospitalSelector }) => {
  const isMobile = useMediaQuery('(max-width:599px)');
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(cases.length / PAGE_SIZE));
  const paginated  = cases.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <div className="ar-page-header">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap', gap: 1 }}>
          <div>
            <Typography className="ar-page-title">Signed Off Cases</Typography>
            <Typography className="ar-page-sub">Read-only archive of hospital approved and finalized cases</Typography>
          </div>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
            {hospitalSelector}
            <Tooltip title="Refresh list">
              <IconButton onClick={onRefresh} sx={{ color: '#3D7CC9', background: '#fff', border: '1px solid #dde3ef' }}>
                <RefreshOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </div>

      {loading ? (
        <div className="ar-loading-box">
          <CircularProgress size={32} sx={{ color: '#137a3f' }} />
          <span>Fetching signed off cases...</span>
        </div>
      ) : cases.length === 0 ? (
        <div className="ar-panel"><div className="ar-empty">No signed-off cases found.</div></div>
      ) : isMobile ? (
        <>
          {paginated.map((c) => {
            const appId = c.appId || c.app;
            const pt    = c.patientName || c.pt;
            const hs    = c.hospital_share_total != null ? c.hospital_share_total : (c.hs || 0);
            const gs    = c.ghv_share_total != null ? c.ghv_share_total : (c.gs || 0);

            return (
              <div key={appId} className="ar-case-card" onClick={() => onView(appId, c)} style={{ cursor: 'pointer' }}>
                <div className="ar-case-card-header">
                  <div>
                    <div className="ar-case-card-app">{appId}</div>
                    <div className="ar-case-card-patient">{pt}</div>
                    <div className="ar-case-card-date">{fmtDate(c.dateOfAdmission || c.apd)}</div>
                  </div>
                  <span className="ar-pill signed">Signed Off</span>
                </div>
                <div className="ar-case-card-shares">
                  <div className="ar-case-card-share"><small>Hospital Share</small><b>{inr(hs)}</b></div>
                  <div className="ar-case-card-share"><small>GHV Share</small><b>{inr(gs)}</b></div>
                </div>
              </div>
            );
          })}
          <PaginationBar page={page} totalPages={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="ar-table-card">
          <div className="ar-table-header">
            <span className="ar-table-header-title">
              {cases.length} signed-off case{cases.length !== 1 ? 's' : ''}
              {totalPages > 1 && <span style={{ fontWeight: 400, color: '#7a84a3', fontSize: '12px', marginLeft: 8 }}>· Page {page} of {totalPages}</span>}
            </span>
          </div>
          <div className="ar-table-wrap">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['App ID', 'Patient Name', 'Admission Date', 'Hospital Share', 'GHV Share', 'Status', 'Action'].map((h) => (
                      <TableCell key={h} sx={thSx}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((c, idx) => {
                    const appId = c.appId || c.app;
                    const pt    = c.patientName || c.pt;
                    const apd   = fmtDate(c.dateOfAdmission || c.apd);
                    const hs    = c.hospital_share_total != null ? c.hospital_share_total : (c.hs || 0);
                    const gs    = c.ghv_share_total != null ? c.ghv_share_total : (c.gs || 0);

                    return (
                      <TableRow
                        key={appId}
                        hover
                        sx={{
                          '&:last-child td': { borderBottom: 0 },
                          background: idx % 2 === 0 ? '#fff' : '#fafbfd',
                          cursor: 'pointer',
                        }}
                        onClick={() => onView(appId, c)}
                      >
                        <TableCell sx={{ fontWeight: 700, color: '#0d1f35', py: 1.8, px: 2 }}>{appId}</TableCell>
                        <TableCell sx={{ fontWeight: 500, py: 1.8, px: 2 }}>{pt}</TableCell>
                        <TableCell sx={{ color: '#7a84a3', py: 1.8, px: 2 }}>{apd}</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#5a12cc', py: 1.8, px: 2 }}>{inr(hs)}</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#008a6e', py: 1.8, px: 2 }}>{inr(gs)}</TableCell>
                        <TableCell sx={{ py: 1.8, px: 2 }}>
                          <span className="ar-pill signed">Signed Off</span>
                        </TableCell>
                        <TableCell sx={{ py: 1.8, px: 2 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => { e.stopPropagation(); onView(appId, c); }}
                            sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '11px', borderColor: '#dde3ef' }}
                          >
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <PaginationBar page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        </div>
      )}
    </>
  );
};

// ─── Raised Disputes View ─────────────────────────────────────────────────────
const ARDisputes = ({ cases, loading, onRefresh, onView, hospitalSelector }) => {
  const isMobile = useMediaQuery('(max-width:599px)');
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(cases.length / PAGE_SIZE));
  const paginated  = cases.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <div className="ar-page-header">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap', gap: 1 }}>
          <div>
            <Typography className="ar-page-title">Raised Disputes</Typography>
            <Typography className="ar-page-sub">Disputes raised by you, actively under review by Pristyn Care</Typography>
          </div>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
            {hospitalSelector}
            <Tooltip title="Refresh list">
              <IconButton onClick={onRefresh} sx={{ color: '#3D7CC9', background: '#fff', border: '1px solid #dde3ef' }}>
                <RefreshOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </div>

      {loading ? (
        <div className="ar-loading-box">
          <CircularProgress size={32} sx={{ color: '#b75c00' }} />
          <span>Fetching raised disputes...</span>
        </div>
      ) : cases.length === 0 ? (
        <div className="ar-panel"><div className="ar-empty">No disputes raised yet.</div></div>
      ) : isMobile ? (
        <>
          {paginated.map((c) => {
            const appId = c.appId || c.app;
            const pt    = c.patientName || c.pt;
            const hs    = c.hospital_share_total != null ? c.hospital_share_total : (c.hs || 0);
            const lastRemark = (c.hospitalActionTrail && c.hospitalActionTrail[c.hospitalActionTrail.length - 1]?.remark) || c.remark || c.rm || 'Dispute under review';

            return (
              <div key={appId} className="ar-case-card" onClick={() => onView(appId, c)} style={{ cursor: 'pointer' }}>
                <div className="ar-case-card-header">
                  <div>
                    <div className="ar-case-card-app">{appId}</div>
                    <div className="ar-case-card-patient">{pt}</div>
                    <div className="ar-case-card-date">{fmtDate(c.dateOfAdmission || c.apd)}</div>
                  </div>
                  <span className="ar-pill disputed">Under Review</span>
                </div>
                <Box sx={{ mb: 1.5, p: '9px 12px', background: '#fff4e5', border: '1px solid #ffe0b2', borderRadius: '8px', fontSize: '12px', color: '#5d3a00', lineHeight: 1.5 }}>
                  {lastRemark}
                </Box>
                <div className="ar-case-card-shares">
                  <div className="ar-case-card-share"><small>Hospital Share</small><b>{inr(hs)}</b></div>
                </div>
              </div>
            );
          })}
          <PaginationBar page={page} totalPages={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="ar-table-card">
          <div className="ar-table-header">
            <span className="ar-table-header-title">
              {cases.length} dispute{cases.length !== 1 ? 's' : ''} raised
              {totalPages > 1 && <span style={{ fontWeight: 400, color: '#7a84a3', fontSize: '12px', marginLeft: 8 }}>· Page {page} of {totalPages}</span>}
            </span>
          </div>
          <div className="ar-table-wrap">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['App ID', 'Patient Name', 'Admission Date', 'Hospital Share', 'Remarks', 'Status', 'Action'].map(
                      (h) => <TableCell key={h} sx={thSx}>{h}</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((c, idx) => {
                    const appId = c.appId || c.app;
                    const pt    = c.patientName || c.pt;
                    const apd   = fmtDate(c.dateOfAdmission || c.apd);
                    const hs    = c.hospital_share_total != null ? c.hospital_share_total : (c.hs || 0);
                    const lastRemark = (c.hospitalActionTrail && c.hospitalActionTrail[c.hospitalActionTrail.length - 1]?.remark) || c.remark || c.rm || 'Dispute under review';

                    return (
                      <TableRow
                        key={appId}
                        hover
                        sx={{
                          '&:last-child td': { borderBottom: 0 },
                          background: idx % 2 === 0 ? '#fff' : '#fafbfd',
                          cursor: 'pointer',
                        }}
                        onClick={() => onView(appId, c)}
                      >
                        <TableCell sx={{ fontWeight: 700, color: '#3D7CC9', py: 1.8, px: 2 }}>{appId}</TableCell>
                        <TableCell sx={{ fontWeight: 500, py: 1.8, px: 2 }}>{pt}</TableCell>
                        <TableCell sx={{ color: '#7a84a3', py: 1.8, px: 2 }}>{apd}</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#5a12cc', py: 1.8, px: 2 }}>{inr(hs)}</TableCell>
                        <TableCell sx={{ maxWidth: 260, whiteSpace: 'normal', fontSize: '12px', color: '#5d3a00', py: 1.8, px: 2 }}>
                          {lastRemark}
                        </TableCell>
                        <TableCell sx={{ py: 1.8, px: 2 }}>
                          <span className="ar-pill disputed">Under Review</span>
                        </TableCell>
                        <TableCell sx={{ py: 1.8, px: 2 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => { e.stopPropagation(); onView(appId, c); }}
                            sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '11px', borderColor: '#dde3ef' }}
                          >
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <PaginationBar page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        </div>
      )}
    </>
  );
};

// ─── Root Component ────────────────────────────────────────────────────────────
const ARReconciliation = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const authUser = useSelector((state) => state.authentication.user);
  const hospitalsByIds = useSelector((state) => state.hospitals?.hospitalsByIds) || {};

  // Access + hospital ids derived from the logged-in user.
  // Memoize so these do not produce a new array reference on every render
  // (an unstable `hospitalIds` would retrigger effects and cause a fetch loop).
  const canAccess = hasArAccess(authUser);
  const hospitalIds = useMemo(() => getUserHospitalIds(authUser), [authUser]);

  // Dropdown label — show "Name (id)" when we have a name, else just the id
  const hospitalLabel = (id) => {
    const name =
      hospitalsByIds?.[id]?.friendly_name ||
      hospitalsByIds?.[id]?.hospitalName ||
      '';
    return name ? `${name} (${id})` : id;
  };

  // Selected hospital — defaults to the first assigned id
  const [selectedHospitalId, setSelectedHospitalId] = useState(hospitalIds[0] || '');

  // Keep selection valid if the user's hospital list changes (e.g. after hydration)
  useEffect(() => {
    if (hospitalIds.length && !hospitalIds.includes(selectedHospitalId)) {
      setSelectedHospitalId(hospitalIds[0]);
    }
  }, [hospitalIds, selectedHospitalId]);

  // Read state from URL query parameters
  const paramTab = searchParams.get('tab');
  const tab = (paramTab === 'signed' || paramTab === 'disp') ? paramTab : 'dash';
  const detailApp = searchParams.get('appId');

  const [detailSummary, setDetailSummary] = useState(null);

  // Lists state
  const [openCases, setOpenCases]         = useState([]);
  const [signedCases, setSignedCases]     = useState([]);
  const [disputeCases, setDisputeCases]   = useState([]);
  const [loading, setLoading]             = useState(false);

  // Fetch all 3 case lists for the selected hospital only when on AR Reconciliation route
  const fetchAllCases = useCallback(async () => {
    // Strict guard: Do not call API if not on /ar-reconciliation
    if (!location.pathname.includes('/ar-reconciliation')) {
      return;
    }
    // No hospital assigned — nothing to fetch
    if (!selectedHospitalId) {
      setOpenCases([]);
      setSignedCases([]);
      setDisputeCases([]);
      return;
    }

    setLoading(true);
    try {
      const [openRes, signedRes, disputeRes] = await Promise.allSettled([
        getArStatusDataService({ hospitalId: selectedHospitalId }),
        getArStatusDataService({ hospitalId: selectedHospitalId, hospitalStatus: 'approve' }),
        getArStatusDataService({ hospitalId: selectedHospitalId, hospitalStatus: 'dispute' }),
      ]);

      if (openRes.status === 'fulfilled' && openRes.value?.data) {
        setOpenCases(openRes.value.data);
      } else {
        console.warn('Open cases API issue, check backend response', openRes);
      }

      if (signedRes.status === 'fulfilled' && signedRes.value?.data) {
        setSignedCases(signedRes.value.data);
      }

      if (disputeRes.status === 'fulfilled' && disputeRes.value?.data) {
        setDisputeCases(disputeRes.value.data);
      }
    } catch (err) {
      console.error('Error fetching AR data:', err);
      toast.error('Unable to fetch live AR data');
    } finally {
      setLoading(false);
    }
  }, [selectedHospitalId, location.pathname]);

  // Execute ONLY when active path is /ar-reconciliation and the user has access
  useEffect(() => {
    if (canAccess && location.pathname.includes('/ar-reconciliation')) {
      dispatch(homeStateActions.setActiveTab('ar-reconciliation'));
      fetchAllCases();
    }
  }, [location.pathname, fetchAllCases, dispatch, canAccess]);

  const handleTabChange = (newTab) => {
    const next = new URLSearchParams();
    if (newTab && newTab !== 'dash') {
      next.set('tab', newTab);
    }
    setSearchParams(next);
    setDetailSummary(null);
  };

  const handleViewDetail = (appId, summary) => {
    setDetailSummary(summary || null);
    const next = new URLSearchParams(searchParams);
    next.set('appId', appId);
    setSearchParams(next);
  };

  const handleBackFromDetail = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('appId');
    setSearchParams(next);
    setDetailSummary(null);
  };

  const handleCaseActionSuccess = (appId, action, remark = '') => {
    fetchAllCases();
    const next = new URLSearchParams();
    if (action === 'approve') {
      next.set('tab', 'signed');
    } else if (action === 'dispute') {
      next.set('tab', 'disp');
    }
    setSearchParams(next);
    setDetailSummary(null);
  };

  // Find summary from lists if detailSummary is not yet set (e.g. on direct URL load or refresh)
  const activeSummary =
    detailSummary ||
    openCases.find((c) => (c.appId || c.aid) === detailApp) ||
    signedCases.find((c) => (c.appId || c.aid) === detailApp) ||
    disputeCases.find((c) => (c.appId || c.aid) === detailApp) ||
    null;

  // Route guard — users without ar_dash access cannot open this page directly
  if (!canAccess) {
    return <Navigate to="/home" replace />;
  }

  // Hospital dropdown (only when the user has more than one hospital).
  // Rendered inside each list view's header, to the LEFT of the refresh icon.
  const hospitalSelector = hospitalIds.length > 1 ? (
    <FormControl size="small" sx={{ minWidth: 200 }}>
      <Select
        value={selectedHospitalId}
        onChange={(e) => { setSelectedHospitalId(e.target.value); setDetailSummary(null); }}
        sx={{
          borderRadius: '10px',
          background: '#fff',
          fontSize: '13px',
          fontWeight: 600,
          color: '#0d1f35',
          '& fieldset': { borderColor: '#dde3ef' },
          '&:hover fieldset': { borderColor: '#3D7CC9' },
        }}
      >
        {hospitalIds.map((id) => (
          <MenuItem key={id} value={id} sx={{ fontSize: '13px' }}>
            {hospitalLabel(id)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  ) : null;

  return (
    <div className="ar-layout">
      <Sidebar
        tab={tab}
        setTab={handleTabChange}
      />
      <div className="ar-main">
        {detailApp ? (
          <ARDetail
            appId={detailApp}
            caseSummary={activeSummary}
            hospitalId={selectedHospitalId}
            fromTab={tab}
            onBack={handleBackFromDetail}
            onCaseActionSuccess={handleCaseActionSuccess}
          />
        ) : hospitalIds.length === 0 ? (
          <div className="ar-panel">
            <div className="ar-empty">No hospital is assigned to your account.</div>
          </div>
        ) : (
          <>
            {tab === 'dash' && (
              <ARDashboard
                cases={openCases}
                loading={loading}
                onRefresh={fetchAllCases}
                onView={handleViewDetail}
                openCount={openCases.length}
                signedCount={signedCases.length}
                disputeCount={disputeCases.length}
                onTabSelect={handleTabChange}
                hospitalSelector={hospitalSelector}
              />
            )}
            {tab === 'signed' && (
              <ARSignedOff
                cases={signedCases}
                loading={loading}
                onRefresh={fetchAllCases}
                onView={handleViewDetail}
                hospitalSelector={hospitalSelector}
              />
            )}
            {tab === 'disp' && (
              <ARDisputes
                cases={disputeCases}
                loading={loading}
                onRefresh={fetchAllCases}
                onView={handleViewDetail}
                hospitalSelector={hospitalSelector}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ARReconciliation;
